// src/components/SupplierManagement.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  orderBy,
  updateDoc // <-- Importa updateDoc
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next'; 

// --- IMPORT MUI ---
import {
  Box,
  Typography, 
  IconButton,
  CircularProgress, Paper, Button, TextField, List,
  ListItem, ListItemText, ListItemIcon, 
  // --- Aggiungi componenti Dialog ---
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
  // --- Fine Aggiunta ---
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit'; // <-- Importa EditIcon

// --- Aggiungi il Modale di Modifica ---
const EditSupplierModal = ({ open, onClose, supplier, onSave, t }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    notes: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (supplier) {
      setFormData({
        companyName: supplier.companyName || '',
        contactPerson: supplier.contactPerson || '',
        phone: supplier.phone || '',
        email: supplier.email || '',
        notes: supplier.notes || ''
      });
    }
  }, [supplier]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.companyName || !supplier) return;
    setIsSaving(true);
    try {
      await onSave(supplier.id, formData);
      onClose();
    } catch (error) {
      console.error("Error updating supplier:", error);
      alert(t('edit_error')); // Aggiungi 'edit_error' al tuo file JSON di traduzione
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('edit_title')}</DialogTitle> {/* Aggiungi 'edit_title' */}
      <DialogContent>
        <Grid container spacing={2.5} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              autoFocus
              name="companyName"
              label={t('company_name')}
              type="text"
              fullWidth
              required
              value={formData.companyName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="contactPerson"
              label={t('contact_person')}
              type="text"
              fullWidth
              value={formData.contactPerson}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="phone"
              label={t('phone')}
              type="tel"
              fullWidth
              value={formData.phone}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="email"
              label={t('email')}
              type="email"
              fullWidth
              value={formData.email}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="notes"
              label={t('notes')}
              type="text"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>{t('cancel_button')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving || !formData.companyName}>
          {isSaving ? <CircularProgress size={24} /> : t('save_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// --- Fine Modale ---


function SupplierManagement({ teamId, userRole }) {
  // --- MODIFICA: Inizializza t() ---
  const { t } = useTranslation('supplier');
  // --- FINE MODIFICA ---

  // Stati (invariati)
  const [suppliers, setSuppliers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canManage = userRole === 'admin' || userRole === 'Engineer' || userRole === 'Magazziniere';

  // --- Aggiungi stati per il modale ---
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentSupplier, setCurrentSupplier] = useState(null);
  // --- Fine Aggiunta ---

  // useEffect (invariato)
  useEffect(() => {
    if (!teamId) { setIsLoading(false); return; }
    setIsLoading(true);
    
    const suppliersQuery = query(
      collection(db, "suppliers"),
      where("teamId", "==", teamId),
      orderBy("companyName", "asc")
    );

    const unsubscribe = onSnapshot(suppliersQuery, (querySnapshot) => {
      setSuppliers(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIsLoading(false);
    }, (error) => {
      console.error("Errore caricamento fornitori (serve indice?):", error.message);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [teamId]);

  // --- MODIFICA: Funzioni Handler (con t() per alert) ---
  const handleAddSupplier = async (e) => {
    e.preventDefault();
    if (!companyName) {
        alert(t('error_mandatory')); // Tradotto
        return;
    }
    
    if (suppliers.some(s => s.companyName.toLowerCase() === companyName.toLowerCase())) {
        alert(t('error_exists')); // Tradotto
        return;
    }

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "suppliers"), {
        teamId: teamId,
        companyName: companyName.trim(),
        contactPerson: contactPerson.trim(),
        phone: phone.trim(),
        email: email.trim(),
        notes: notes.trim()
      });
      setCompanyName(''); setContactPerson(''); setPhone(''); setEmail(''); setNotes('');
    } catch (error) {
      console.error("Errore aggiunta fornitore:", error);
      alert(t('add_error')); // Tradotto
    }
    setIsSubmitting(false);
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (window.confirm(t('delete_confirm'))) { // Tradotto
      try {
        await deleteDoc(doc(db, "suppliers", supplierId));
      } catch (error) {
        console.error("Errore eliminazione fornitore:", error);
        alert(t('delete_error')); // Tradotto
      }
    }
  };
  // --- FINE MODIFICA ---
  
  // --- Aggiungi funzioni per modale ---
  const handleOpenEditModal = (supplier) => {
    setCurrentSupplier(supplier);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setCurrentSupplier(null);
    setIsEditModalOpen(false);
  };

  const handleUpdateSupplier = async (supplierId, formData) => {
    const trimmedName = formData.companyName.trim();
    if (suppliers.some(s => s.companyName.toLowerCase() === trimmedName.toLowerCase() && s.id !== supplierId)) {
      alert(t('error_exists'));
      throw new Error("Supplier name already exists");
    }
    const supplierRef = doc(db, "suppliers", supplierId);
    await updateDoc(supplierRef, {
        companyName: trimmedName,
        contactPerson: formData.contactPerson.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        notes: formData.notes.trim()
    });
  };
  // --- Fine Aggiunta ---

  // --- RENDER (MODIFICATO con t()) ---
  return (
    <>
      {/* 1. Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('title')} {/* Tradotto */}
        </Typography>
      </Paper>
      
      {/* 2. Paper Form Aggiungi Fornitore */}
      {/* === INIZIO BLOCCO RIPRISTINATO === */}
      {canManage && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3,
            border: '1px solid', 
            borderColor: 'divider', 
            backgroundColor: 'background.paper' 
          }}
        >
          <Box component="form" onSubmit={handleAddSupplier} sx={{ mb: 1 }}>
            <Typography variant="h5" gutterBottom>{t('add_title')}</Typography>
            
            {/* RIGA 1: Nome e Contatto */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
              <TextField 
                label={t('company_name')}
                value={companyName} 
                onChange={(e) => setCompanyName(e.target.value)} 
                required 
                fullWidth 
                sx={{ flex: 1 }}
              />
              <TextField 
                label={t('contact_person')}
                value={contactPerson} 
                onChange={(e) => setContactPerson(e.target.value)} 
                fullWidth 
                sx={{ flex: 1 }}
              />
            </Box>
            
            {/* RIGA 2: Telefono e Email */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
              <TextField 
                label={t('phone')}
                value={phone} 
                onChange={(e) => setPhone(e.target.value)} 
                fullWidth 
                sx={{ flex: 1 }}
              />
              <TextField 
                label={t('email')}
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                fullWidth 
                sx={{ flex: 1 }}
              />
            </Box>
            
            {/* RIGA 3: Note */}
            <Box sx={{ display: 'flex', mb: 2.5 }}>
              <TextField 
                label={t('notes')}
                value={notes} 
                onChange={(e) => setNotes(e.target.value)} 
                fullWidth 
                multiline 
                rows={2} 
              />
            </Box>
            
            <Button 
              type="submit" 
              variant="contained" 
              disabled={isSubmitting || !companyName} 
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />} 
              size="large"
              sx={{ height: '56px' }}
            >
              {t('add_button')} {/* Tradotto */}
            </Button>
          </Box>
        </Paper>
      )}
      {/* === FINE BLOCCO RIPRISTINATO === */}
      
      {/* 3. Paper Lista Fornitori */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 },
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        <Typography variant="h5" gutterBottom>{t('saved_title')}</Typography>
        {isLoading && <CircularProgress size={24} />}
        {!isLoading && suppliers.length === 0 && (
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t('empty')}</Typography>
        )}
        {!isLoading && suppliers.length > 0 && (
          <List>
            {suppliers.map((supplier) => (
              <ListItem
                key={supplier.id}
                divider
                sx={{ 
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 1
                }}
                secondaryAction={
                  canManage ? (
                    // --- Aggiungi Box per icone ---
                    <Box>
                      <IconButton edge="end" aria-label="edit" onClick={() => handleOpenEditModal(supplier)} sx={{ mr: 0.5 }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteSupplier(supplier.id)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Box>
                    // --- Fine Box ---
                  ) : null
                }
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText
                  primary={supplier.companyName}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                  secondary={
                    <>
                      {supplier.contactPerson && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>{t('list_ref')} {supplier.contactPerson}</Typography>}
                      {supplier.phone && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>{t('list_phone')} {supplier.phone}</Typography>}
                      {supplier.email && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>{t('list_email')} {supplier.email}</Typography>}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* --- Aggiungi il modale al render --- */}
      <EditSupplierModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        supplier={currentSupplier}
        onSave={handleUpdateSupplier}
        t={t}
      />
    </>
  );
}

export default SupplierManagement;